import { useCallback, useEffect } from 'react';

export type KeyboardShortcut = {
	key: string;
	description: string;
	action: () => void;
	ctrl?: boolean;
	meta?: boolean;
	shift?: boolean;
	// Don't trigger when editing text
	ignoreWhenEditing?: boolean;
};

type UseKeyboardShortcutsOptions = {
	enabled?: boolean;
	shortcuts: KeyboardShortcut[];
};

export function useKeyboardShortcuts({ enabled = true, shortcuts }: UseKeyboardShortcutsOptions) {
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!enabled) return;

			// Check if user is typing in an input/textarea
			const isEditing =
				e.target instanceof HTMLInputElement ||
				e.target instanceof HTMLTextAreaElement ||
				(e.target instanceof HTMLElement && e.target.isContentEditable);

			for (const shortcut of shortcuts) {
				// Skip if editing and shortcut should be ignored
				if (isEditing && shortcut.ignoreWhenEditing !== false) continue;

				const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();
				const ctrlMatch = shortcut.ctrl ? e.ctrlKey : !e.ctrlKey;
				const metaMatch = shortcut.meta ? e.metaKey : !e.metaKey;
				const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;

				if (keyMatch && ctrlMatch && metaMatch && shiftMatch) {
					e.preventDefault();
					shortcut.action();
					return;
				}
			}
		},
		[enabled, shortcuts]
	);

	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [handleKeyDown]);
}
